/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable theme-colors/no-literal-colors */
import React from 'react';
import { SupersetTheme, t } from '@superset-ui/core';
import { FormItem, FormProps } from 'src/components/Form';
import { InputNumber } from 'src/components/Input';
import Select from 'src/components/Select/Select';
import {
  COMPARATOR,
  ConditionalFormattingConfig,
  MULTIPLE_VALUE_COMPARATORS,
} from './types';

export const colorSchemeOptions = (theme: SupersetTheme) =>
  [
    { value: theme.colors.success.light1, label: t('浅绿色') },
    { value: theme.colors.alert.light1, label: t('黄色') },
    { value: theme.colors.error.light1, label: t('浅红色') },
    { value: theme.colors.success.dark1, label: t('深绿色') },
    { value: theme.colors.alert.dark1, label: t('深棕色') },
    { value: theme.colors.error.dark1, label: t('深红色') },
    { value: 'custom', label: t('自定义颜色') },
  ].map(option => ({
    ...option,
    label:
      typeof option.label === 'string'
        ? option.label
        : JSON.stringify(option.label),
    value:
      typeof option.value === 'string'
        ? option.value
        : JSON.stringify(option.value),
  }));

export const operatorOptions = [
  { value: COMPARATOR.NONE, label: t('None') },
  { value: COMPARATOR.GREATER_THAN, label: '>' },
  { value: COMPARATOR.LESS_THAN, label: '<' },
  { value: COMPARATOR.GREATER_OR_EQUAL, label: '≥' },
  { value: COMPARATOR.LESS_OR_EQUAL, label: '≤' },
  { value: COMPARATOR.EQUAL, label: '=' },
  { value: COMPARATOR.NOT_EQUAL, label: '≠' },
  { value: COMPARATOR.BETWEEN, label: '< x <' },
  { value: COMPARATOR.BETWEEN_OR_EQUAL, label: '≤ x ≤' },
  { value: COMPARATOR.BETWEEN_OR_LEFT_EQUAL, label: '≤ x <' },
  { value: COMPARATOR.BETWEEN_OR_RIGHT_EQUAL, label: '< x ≤' },
];

export const displayTypeOptions = [
  { label: t('无（仅颜色）'), value: 'none' },
  { label: t('数据条'), value: 'progressBar' },
  { label: t('迷你柱状图'), value: 'miniBarChart' },
  { label: t('圆环进度图'), value: 'donutProgress' },
  { label: t('图标集'), value: 'iconSet' },
  { label: t('迷你饼图'), value: 'miniPieChart' },
  { label: t('子弹图'), value: 'bulletChart' },
  { label: t('按钮样式'), value: 'button' },
];

export const iconSetTypeOptions = [
  { label: t('三向箭头 (↑→↓)'), value: 'arrows3' },
  { label: t('四向箭头 (↑↓)'), value: 'arrows4' },
  { label: t('五向箭头'), value: 'arrows5' },
  { label: t('交通灯 (红黄绿)'), value: 'traffic' },
  { label: t('星级'), value: 'stars' },
];

export const rulesRequired = [{ required: true, message: t('Required') }];

const targetValueValidator =
  (
    compare: (targetValue: number, compareValue: number) => boolean,
    rejectMessage: string,
  ) =>
  (targetValue: number | string) =>
  (_: unknown, compareValue: number | string) => {
    if (
      !targetValue ||
      !compareValue ||
      compare(Number(targetValue), Number(compareValue))
    ) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(rejectMessage));
  };

const targetValueLeftValidator = targetValueValidator(
  (target: number, val: number) => target > val,
  t('This value should be smaller than the right target value'),
);

const targetValueRightValidator = targetValueValidator(
  (target: number, val: number) => target < val,
  t('This value should be greater than the left target value'),
);

type GetFieldValue = Pick<Required<FormProps>['form'], 'getFieldValue'>;

const rulesTargetValueLeft = [
  { required: true, message: t('Required') },
  ({ getFieldValue }: GetFieldValue) => ({
    validator: targetValueLeftValidator(getFieldValue('targetValueRight')),
  }),
];

const rulesTargetValueRight = [
  { required: true, message: t('Required') },
  ({ getFieldValue }: GetFieldValue) => ({
    validator: targetValueRightValidator(getFieldValue('targetValueLeft')),
  }),
];

const targetValueLeftDeps = ['targetValueRight'];
const targetValueRightDeps = ['targetValueLeft'];

export const shouldOperatorFieldsUpdate = (
  prevValues: ConditionalFormattingConfig,
  currentValues: ConditionalFormattingConfig,
) =>
  isOperatorNone(prevValues.operator) !==
    isOperatorNone(currentValues.operator) ||
  isOperatorMultiValue(prevValues.operator) !==
    isOperatorMultiValue(currentValues.operator);

export const isOperatorMultiValue = (operator?: COMPARATOR) =>
  Boolean(operator && MULTIPLE_VALUE_COMPARATORS.includes(operator));

export const isOperatorNone = (operator?: COMPARATOR) =>
  !operator || operator === COMPARATOR.NONE;

export const renderOperatorFields = ({ getFieldValue }: GetFieldValue) => {
  const operatorSelect = (
    <FormItem name="operator" label={t('Operator')} rules={rulesRequired}>
      <Select ariaLabel={t('Operator')} options={operatorOptions} />
    </FormItem>
  );

  if (isOperatorNone(getFieldValue('operator'))) {
    return operatorSelect;
  }

  if (isOperatorMultiValue(getFieldValue('operator'))) {
    return (
      <>
        <FormItem
          name="targetValueLeft"
          label={t('Left value')}
          rules={rulesTargetValueLeft}
          dependencies={targetValueLeftDeps}
          validateTrigger="onBlur"
          trigger="onBlur"
        >
          <InputNumber style={{ width: '100%' }} />
        </FormItem>
        {operatorSelect}
        <FormItem
          name="targetValueRight"
          label={t('Right value')}
          rules={rulesTargetValueRight}
          dependencies={targetValueRightDeps}
          validateTrigger="onBlur"
          trigger="onBlur"
        >
          <InputNumber style={{ width: '100%' }} />
        </FormItem>
      </>
    );
  }

  return (
    <>
      {operatorSelect}
      <FormItem
        name="targetValue"
        label={t('Target value')}
        rules={rulesRequired}
      >
        <InputNumber style={{ width: '100%' }} />
      </FormItem>
    </>
  );
};

export const integerFieldRules = (min: number, max: number) => [
  { required: true, message: t('Required') },
  {
    type: 'number' as const,
    min,
    max,
    message: `请输入 ${min}-${max} 之间的整数`,
  },
  {
    validator: (_: unknown, value: number) => {
      if (value !== undefined && value !== null && !Number.isInteger(value)) {
        return Promise.reject(new Error(t('请输入整数')));
      }
      return Promise.resolve();
    },
  },
];

function rgbObjectToHex(color: { r: number; g: number; b: number }) {
  const { r, g, b } = color;
  const redHex = r.toString(16).padStart(2, '0').toUpperCase();
  const greenHex = g.toString(16).padStart(2, '0').toUpperCase();
  const blueHex = b.toString(16).padStart(2, '0').toUpperCase();
  return `#${redHex}${greenHex}${blueHex}`;
}

function normalizeRgbField(
  value: ConditionalFormattingConfig[keyof ConditionalFormattingConfig],
) {
  if (typeof value === 'object' && value !== null && 'r' in value) {
    return rgbObjectToHex(value as { r: number; g: number; b: number });
  }
  return value;
}

export function normalizeFormattingFormValues(
  values: ConditionalFormattingConfig,
): ConditionalFormattingConfig {
  const newValues = { ...values };
  const displayType = newValues.displayType || 'none';

  newValues.showProgressBar = displayType === 'progressBar';
  newValues.showMiniBarChart = displayType === 'miniBarChart';
  newValues.showDonutProgress = displayType === 'donutProgress';
  newValues.showIconSet = displayType === 'iconSet';
  newValues.showMiniPieChart = displayType === 'miniPieChart';
  newValues.showBulletChart = displayType === 'bulletChart';

  newValues.differenceColor = normalizeRgbField(
    newValues.differenceColor,
  ) as string;
  newValues.bulletChartTargetColor = normalizeRgbField(
    newValues.bulletChartTargetColor,
  ) as string;
  newValues.buttonBgColor = normalizeRgbField(
    newValues.buttonBgColor,
  ) as string;

  if (newValues.colorScheme === 'custom') {
    if (
      typeof newValues.customColor === 'object' &&
      newValues.customColor !== null &&
      'r' in newValues.customColor
    ) {
      newValues.colorScheme = rgbObjectToHex(newValues.customColor);
      delete newValues.customColor;
    }
  }

  return newValues;
}

function inferDisplayType(config?: ConditionalFormattingConfig) {
  if (config?.displayType) {
    return config.displayType;
  }
  if (config?.showProgressBar) return 'progressBar';
  if (config?.showMiniBarChart) return 'miniBarChart';
  if (config?.showDonutProgress) return 'donutProgress';
  if (config?.showIconSet) return 'iconSet';
  if (config?.showMiniPieChart) return 'miniPieChart';
  if (config?.showBulletChart) return 'bulletChart';
  return 'none';
}

export function buildFormattingInitialValues(
  theme: SupersetTheme,
  columns: { label: string; value: string }[],
  config?: ConditionalFormattingConfig,
) {
  const schemes = colorSchemeOptions(theme);
  const displayType = inferDisplayType(config);
  return {
    column: columns[0]?.value,
    colorScheme: schemes[0].value,
    operator: operatorOptions[0].value,
    enableColorScale: false,
    showSubscript: false,
    achievedText: '',
    showDifference: false,
    differenceColor: theme.colors.primary.base,
    filterZero: false,
    differenceTargetValue: undefined,
    differenceCalculation: 'actual_minus_target',
    progressBarStyle: 'classic',
    progressBarWidth: 6,
    iconSetType: 'arrows3',
    miniBarChartStyle: 'vertical',
    miniBarChartWidth: 10,
    miniBarChartHeight: 20,
    donutSize: 16,
    donutThickness: 3,
    displayType,
    miniPieChartSize: 18,
    bulletChartWidth: 55,
    bulletChartHeight: 12,
    bulletChartTargetColor: theme.colors.grayscale.dark1,
    bulletChartShowTarget: false,
    buttonStyle: 'solid',
    buttonBgColor: undefined,
    buttonRadius: 4,
    buttonPadding: 4,
    buttonBorderWidth: 1,
    ...config,
  };
}
